import React, { useEffect, useState } from "react";

import Grid from "@material-ui/core/Grid";
import MenuItem from "@material-ui/core/MenuItem";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Select from "@material-ui/core/Select";
import FormHelperText from "@material-ui/core/FormHelperText";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";

import { makeStyles } from "@material-ui/core/styles";

import toastError from "../../errors/toastError";
import useSettings from "../../hooks/useSettings";
import { i18n } from "../../translate/i18n";

const PRODUCTION_WEBHOOK_URL = "https://ccontrole.com.br/api/cchat-comercio/webhook";
const HOMOLOGATION_WEBHOOK_URL = "http://ccontrole.closmaq/api/cchat-comercio/webhook";
const DEFAULT_TIMEOUT_MS = "8000";

const useStyles = makeStyles((theme) => ({
  header: {
    marginBottom: theme.spacing(3),
  },
  title: {
    fontWeight: 600,
  },
  subtitle: {
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5),
  },
  selectContainer: {
    width: "100%",
    textAlign: "left",
  },
}));

export default function ApiConfig({ settings }) {
  const classes = useStyles();
  const { update } = useSettings();

  const [environment, setEnvironment] = useState("production");
  const [productApiEnabled, setProductApiEnabled] = useState("enabled");
  const [productWebhookUrl, setProductWebhookUrl] = useState(PRODUCTION_WEBHOOK_URL);
  const [productTimeoutMs, setProductTimeoutMs] = useState(DEFAULT_TIMEOUT_MS);
  const [replyNoResults, setReplyNoResults] = useState("disabled");
  const [replyGroups, setReplyGroups] = useState("disabled");
  const [loadingKey, setLoadingKey] = useState("");

  useEffect(() => {
    if (!Array.isArray(settings)) return;

    const getSetting = (key, fallback) => {
      const setting = settings.find((item) => item.key === key);
      return setting ? setting.value : fallback;
    };

    const currentEnvironment = getSetting("ccontroleComercioEnvironment", "production");
    const normalizedEnvironment = currentEnvironment === "homologation" ? "homologation" : "production";

    setEnvironment(normalizedEnvironment);
    setProductApiEnabled(
      normalizedEnvironment === "production"
        ? "enabled"
        : getSetting("ccontroleComercioEnabled", "enabled")
    );
    const savedWebhookUrl = getSetting("ccontroleComercioWebhookUrl", HOMOLOGATION_WEBHOOK_URL);
    setProductWebhookUrl(
      normalizedEnvironment === "production"
        ? PRODUCTION_WEBHOOK_URL
        : (!savedWebhookUrl || savedWebhookUrl === PRODUCTION_WEBHOOK_URL
          ? HOMOLOGATION_WEBHOOK_URL
          : savedWebhookUrl)
    );
    setProductTimeoutMs(
      normalizedEnvironment === "production"
        ? DEFAULT_TIMEOUT_MS
        : getSetting("ccontroleComercioTimeoutMs", DEFAULT_TIMEOUT_MS)
    );
    setReplyNoResults(
      normalizedEnvironment === "production"
        ? "disabled"
        : getSetting("ccontroleComercioReplyNoResults", "disabled")
    );
    setReplyGroups(
      normalizedEnvironment === "production"
        ? "disabled"
        : getSetting("ccontroleComercioReplyGroups", "disabled")
    );
  }, [settings]);

  async function saveSetting(key, value, setter) {
    setter(value);
    setLoadingKey(key);

    try {
      await update({ key, value });
    } catch (err) {
      toastError(err);
    } finally {
      setLoadingKey("");
    }
  }

  async function saveMultipleSettings(changes) {
    setLoadingKey("ccontroleComercioEnvironment");

    try {
      await Promise.all(
        changes.map((change) => update({ key: change.key, value: change.value }))
      );
    } catch (err) {
      toastError(err);
    } finally {
      setLoadingKey("");
    }
  }

  const isProduction = environment !== "homologation";

  const changeEnvironment = async (value) => {
    const nextEnvironment = value === "homologation" ? "homologation" : "production";
    setEnvironment(nextEnvironment);

    if (nextEnvironment === "production") {
      setProductApiEnabled("enabled");
      setProductWebhookUrl(PRODUCTION_WEBHOOK_URL);
      setProductTimeoutMs(DEFAULT_TIMEOUT_MS);
      setReplyNoResults("disabled");
      setReplyGroups("disabled");

      await saveMultipleSettings([
        { key: "ccontroleComercioEnvironment", value: "production" },
        { key: "ccontroleComercioEnabled", value: "enabled" },
        { key: "ccontroleComercioWebhookUrl", value: PRODUCTION_WEBHOOK_URL },
        { key: "ccontroleComercioTimeoutMs", value: DEFAULT_TIMEOUT_MS },
        { key: "ccontroleComercioReplyNoResults", value: "disabled" },
        { key: "ccontroleComercioReplyGroups", value: "disabled" }
      ]);
      return;
    }

    const nextWebhookUrl =
      productWebhookUrl && productWebhookUrl !== PRODUCTION_WEBHOOK_URL
        ? productWebhookUrl
        : HOMOLOGATION_WEBHOOK_URL;

    setProductWebhookUrl(nextWebhookUrl);

    await saveMultipleSettings([
      { key: "ccontroleComercioEnvironment", value: "homologation" },
      { key: "ccontroleComercioWebhookUrl", value: nextWebhookUrl }
    ]);
  };

  const saveWebhookUrl = (value) => {
    const nextValue = value.trim() || HOMOLOGATION_WEBHOOK_URL;
    saveSetting("ccontroleComercioWebhookUrl", nextValue, setProductWebhookUrl);
  };

  const saveTimeout = (value) => {
    const parsedValue = Number(value);
    const nextValue = Number.isFinite(parsedValue) && parsedValue > 0
      ? String(parsedValue)
      : "8000";

    saveSetting("ccontroleComercioTimeoutMs", nextValue, setProductTimeoutMs);
  };

  return (
    <>
      <div className={classes.header}>
        <Typography variant="h6" className={classes.title}>
          API CONFIG
        </Typography>
        <Typography variant="body2" className={classes.subtitle}>
          Configure a consulta automatica que o CChat executa quando o cliente pergunta por produtos.
        </Typography>
      </div>

      <Grid spacing={3} container>
        <Grid xs={12} sm={6} md={4} item>
          <FormControl className={classes.selectContainer}>
            <InputLabel id="apiEnvironment-label">
              Ambiente
            </InputLabel>
            <Select
              labelId="apiEnvironment-label"
              value={environment}
              onChange={(e) => changeEnvironment(e.target.value)}
            >
              <MenuItem value={"production"}>Produção</MenuItem>
              <MenuItem value={"homologation"}>Homologação</MenuItem>
            </Select>
            <FormHelperText>
              {loadingKey === "ccontroleComercioEnvironment"
                ? i18n.t("settings.settings.options.updating")
                : "Produção fica travada com o endpoint oficial do CControle."}
            </FormHelperText>
          </FormControl>
        </Grid>

        <Grid xs={12} sm={6} md={4} item>
          <FormControl className={classes.selectContainer}>
            <InputLabel id="productApiEnabled-label">
              Consulta automatica de produtos
            </InputLabel>
            <Select
              labelId="productApiEnabled-label"
              value={productApiEnabled}
              disabled={isProduction}
              onChange={(e) => {
                saveSetting(
                  "ccontroleComercioEnabled",
                  e.target.value,
                  setProductApiEnabled
                );
              }}
            >
              <MenuItem value={"disabled"}>{i18n.t("settings.settings.options.disabled")}</MenuItem>
              <MenuItem value={"enabled"}>{i18n.t("settings.settings.options.enabled")}</MenuItem>
            </Select>
            <FormHelperText>
              {loadingKey === "ccontroleComercioEnabled" && i18n.t("settings.settings.options.updating")}
            </FormHelperText>
          </FormControl>
        </Grid>

        <Grid xs={12} sm={6} md={8} item>
          <FormControl className={classes.selectContainer}>
            <TextField
              id="productWebhookUrl"
              name="productWebhookUrl"
              margin="dense"
              label="Webhook URL"
              variant="outlined"
              value={productWebhookUrl}
              disabled={isProduction}
              onChange={(e) => setProductWebhookUrl(e.target.value)}
              onBlur={(e) => saveWebhookUrl(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.currentTarget.blur();
                }
              }}
            />
            <FormHelperText>
              {loadingKey === "ccontroleComercioWebhookUrl"
                ? i18n.t("settings.settings.options.updating")
                : "Endpoint chamado para buscar produtos e montar a resposta automatica."}
            </FormHelperText>
          </FormControl>
        </Grid>

        <Grid xs={12} sm={6} md={4} item>
          <FormControl className={classes.selectContainer}>
            <TextField
              id="productTimeoutMs"
              name="productTimeoutMs"
              margin="dense"
              label="Tempo limite da consulta (ms)"
              variant="outlined"
              type="number"
              value={productTimeoutMs}
              disabled={isProduction}
              inputProps={{ min: 1000, step: 500 }}
              onChange={(e) => setProductTimeoutMs(e.target.value)}
              onBlur={(e) => saveTimeout(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.currentTarget.blur();
                }
              }}
            />
            <FormHelperText>
              {loadingKey === "ccontroleComercioTimeoutMs" && i18n.t("settings.settings.options.updating")}
            </FormHelperText>
          </FormControl>
        </Grid>

        <Grid xs={12} sm={6} md={4} item>
          <FormControl className={classes.selectContainer}>
            <InputLabel id="replyNoResults-label">
              Responder sem resultado
            </InputLabel>
            <Select
              labelId="replyNoResults-label"
              value={replyNoResults}
              disabled={isProduction}
              onChange={(e) => {
                saveSetting(
                  "ccontroleComercioReplyNoResults",
                  e.target.value,
                  setReplyNoResults
                );
              }}
            >
              <MenuItem value={"disabled"}>{i18n.t("settings.settings.options.disabled")}</MenuItem>
              <MenuItem value={"enabled"}>{i18n.t("settings.settings.options.enabled")}</MenuItem>
            </Select>
            <FormHelperText>
              {loadingKey === "ccontroleComercioReplyNoResults"
                ? i18n.t("settings.settings.options.updating")
                : "Mantenha desativado para evitar resposta automatica em saudacoes."}
            </FormHelperText>
          </FormControl>
        </Grid>

        <Grid xs={12} sm={6} md={4} item>
          <FormControl className={classes.selectContainer}>
            <InputLabel id="replyGroups-label">
              Responder em grupos
            </InputLabel>
            <Select
              labelId="replyGroups-label"
              value={replyGroups}
              disabled={isProduction}
              onChange={(e) => {
                saveSetting(
                  "ccontroleComercioReplyGroups",
                  e.target.value,
                  setReplyGroups
                );
              }}
            >
              <MenuItem value={"disabled"}>{i18n.t("settings.settings.options.disabled")}</MenuItem>
              <MenuItem value={"enabled"}>{i18n.t("settings.settings.options.enabled")}</MenuItem>
            </Select>
            <FormHelperText>
              {loadingKey === "ccontroleComercioReplyGroups" && i18n.t("settings.settings.options.updating")}
            </FormHelperText>
          </FormControl>
        </Grid>
      </Grid>
    </>
  );
}
